import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Avatar, Button, Input } from "antd";
import { SendOutlined, UserOutlined } from "@ant-design/icons";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import AppShell from "../../components/app-shell/AppShell";
import useUser from "../../hooks/useUser";
import { getChatSocket } from "../../lib/chatSocket";
import "./member.css";

const formatDay = (value) => {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
};

const formatTime = (value) =>
  new Date(value).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

const MessageThread = ({ endUser }) => {
  const { conversationId } = useParams();
  const queryClient = useQueryClient();
  const bottomRef = useRef(null);
  const [text, setText] = useState("");

  const { getMessages, sendMessage, markConversationRead, getConversations } = useUser();

  const { data: conversations = [] } = useQuery({
    queryKey: ["chat-conversations"],
    queryFn: () => getConversations({ page: 1, limit: 100 }),
    refetchOnWindowFocus: true,
  });

  const currentConversation = useMemo(
    () => conversations.find((x) => String(x._id) === String(conversationId)),
    [conversations, conversationId]
  );

  const otherParticipant = currentConversation?.otherParticipant;

  const {
    data: messagePages,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["chat-messages", conversationId],
    queryFn: ({ pageParam = null }) => getMessages({ conversationId, cursor: pageParam, limit: 30 }),
    enabled: !!conversationId && !!currentConversation,
    initialPageParam: null,
    getNextPageParam: (lastPage) => (lastPage?.hasMore ? lastPage?.nextCursor : undefined),
    refetchOnWindowFocus: true,
  });

  const messages = useMemo(() => {
    const all = (messagePages?.pages || []).flatMap((p) => p?.messages || []);
    return all.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }, [messagePages]);

  const { mutate: markReadAction } = useMutation({
    mutationFn: markConversationRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
    },
  });

  const { mutate: sendMessageAction, isPending: sending } = useMutation({
    mutationFn: sendMessage,
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: ["chat-messages", conversationId] });
      const previous = queryClient.getQueryData(["chat-messages", conversationId]);
      const optimistic = {
        _id: `tmp-${Date.now()}`,
        conversationId: payload.conversationId,
        senderId: {
          _id: endUser?._id,
          name: endUser?.name || "You",
          profilePhoto: endUser?.profilePhoto || "",
        },
        receiverId: { _id: otherParticipant?._id || "" },
        text: payload.text,
        createdAt: new Date().toISOString(),
        status: "sent",
      };

      queryClient.setQueryData(["chat-messages", conversationId], (oldData) => {
        if (!oldData?.pages?.length) {
          return {
            pages: [{ messages: [optimistic], hasMore: false, nextCursor: null }],
            pageParams: [null],
          };
        }
        const pages = [...oldData.pages];
        const lastIdx = pages.length - 1;
        pages[lastIdx] = {
          ...pages[lastIdx],
          messages: [...(pages[lastIdx].messages || []), optimistic],
        };
        return { ...oldData, pages };
      });

      return { previous };
    },
    onSuccess: (savedMessage) => {
      setText("");
      queryClient.setQueryData(["chat-messages", conversationId], (oldData) => {
        if (!oldData?.pages?.length) return oldData;
        const savedId = String(savedMessage?._id || "");
        const alreadyExists = oldData.pages
          .flatMap((p) => p?.messages || [])
          .some((m) => String(m?._id) === savedId);

        const pages = [...oldData.pages];
        const lastIdx = pages.length - 1;
        const cleaned = (pages[lastIdx].messages || []).filter(
          (m) => !String(m?._id).startsWith("tmp-")
        );

        if (alreadyExists) {
          pages[lastIdx] = {
            ...pages[lastIdx],
            messages: cleaned,
          };
          return { ...oldData, pages };
        }

        pages[lastIdx] = {
          ...pages[lastIdx],
          messages: [...cleaned, savedMessage],
        };
        return { ...oldData, pages };
      });
      queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
    },
    onError: (err, _vars, ctx) => {
      queryClient.setQueryData(["chat-messages", conversationId], ctx?.previous);
      toast.error(err?.response?.data?.message || "Failed to send message");
    },
  });

  useEffect(() => {
    if (!conversationId) return;
    markReadAction({ conversationId });
  }, [conversationId, markReadAction]);

  useEffect(() => {
    const socket = getChatSocket();

    const onNewMessage = (message) => {
      if (String(message?.conversationId) !== String(conversationId)) return;
      queryClient.setQueryData(["chat-messages", conversationId], (oldData) => {
        if (!oldData?.pages?.length) {
          return {
            pages: [{ messages: [message], hasMore: false, nextCursor: null }],
            pageParams: [null],
          };
        }
        const exists = (oldData.pages || [])
          .flatMap((p) => p.messages || [])
          .some((x) => String(x._id) === String(message._id));
        if (exists) return oldData;

        const pages = [...oldData.pages];
        const lastIdx = pages.length - 1;
        pages[lastIdx] = {
          ...pages[lastIdx],
          messages: [...(pages[lastIdx].messages || []), message],
        };
        return { ...oldData, pages };
      });

      if (String(message?.receiverId?._id || message?.receiverId) === String(endUser?._id)) {
        markReadAction({ conversationId });
      }
      queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
    };

    const onRead = (payload) => {
      if (String(payload?.conversationId) !== String(conversationId)) return;
      queryClient.invalidateQueries({ queryKey: ["chat-messages", conversationId] });
      queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
    };

    socket.on("chat:new_message", onNewMessage);
    socket.on("chat:messages_read", onRead);
    return () => {
      socket.off("chat:new_message", onNewMessage);
      socket.off("chat:messages_read", onRead);
    };
  }, [conversationId, endUser?._id, markReadAction, queryClient]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);

  const handleSend = () => {
    const trimmed = String(text || "").trim();
    if (!trimmed) return;
    sendMessageAction({ conversationId, text: trimmed });
  };

  const composer =
    conversationId && currentConversation ? (
      <form
        className="chat-composer"
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
      >
        <Input
          placeholder="Message"
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-label="Message"
        />
        <Button
          className="chat-composer__send"
          type="primary"
          htmlType="submit"
          icon={<SendOutlined />}
          loading={sending}
          aria-label="Send"
        />
      </form>
    ) : null;

  return (
    <AppShell
      back="/messages"
      title={otherParticipant?.name || "Chat"}
      actions={
        otherParticipant ? (
          <Avatar
            size={34}
            src={otherParticipant?.profilePhoto || undefined}
            icon={<UserOutlined />}
            style={{ marginRight: 4 }}
          />
        ) : null
      }
      footer={composer}
      hideTabs
      flush
    >
      {!conversationId || !currentConversation ? (
        <div className="app-empty">Loading chat…</div>
      ) : (
        <div className="chat-thread">
          {hasNextPage ? (
            <Button
              size="small"
              style={{ alignSelf: "center" }}
              onClick={() => fetchNextPage()}
              loading={isFetchingNextPage}
            >
              Load older messages
            </Button>
          ) : null}

          {isLoading ? (
            <div className="app-empty">Loading messages…</div>
          ) : messages.length === 0 ? (
            <div className="app-empty">No messages yet. Say hi 👋</div>
          ) : (
            messages.map((m, index) => {
              const mine = String(m?.senderId?._id || m?.senderId) === String(endUser?._id);
              const day = formatDay(m.createdAt);
              const showDay = index === 0 || formatDay(messages[index - 1].createdAt) !== day;
              return (
                <React.Fragment key={m._id}>
                  {showDay ? <div className="chat-day">{day}</div> : null}
                  <div className={`bubble ${mine ? "bubble--mine" : "bubble--theirs"}`}>
                    {m.text}
                    <span className="bubble__time">{formatTime(m.createdAt)}</span>
                  </div>
                </React.Fragment>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>
      )}
    </AppShell>
  );
};

export default MessageThread;
